import { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useUser } from "@/contexts/UserContext/UserContext";
import { useAuthenticatedApiClient } from "./useAuthenticatedApiClient";
import FinancialApplicationApiClient from "@/api/financialApplicationApiClient";
import { useFinancialApplicationCoreData } from "./useFinancialApplicationCoreData";
import { AppToasterV2 } from "@ucl/library";

/**
 * Hook to automatically attach a user to a financial application if they haven't been attached yet.
 * Only attaches if the user is authenticated and is a consumer.
 * 
 * @param financialApplicationId - The ID of the financial application
 * @returns An object containing the attachment status and any error
 */
export const useAttachUser = (financialApplicationId: string) => {
  const { isAuthenticated } = useAuth0();
  const { clientType } = useUser();
  const financialApplicationApiClient = useAuthenticatedApiClient(FinancialApplicationApiClient);
  const { coreData, refreshCoreData } = useFinancialApplicationCoreData(financialApplicationId, false);
  
  const hasAttemptedAttachment = useRef(false);
  const isAttaching = useRef(false);

  const attachUser = async () => {
    if (isAttaching.current) return;
    
    try {
      isAttaching.current = true;
      await financialApplicationApiClient.attachUser(financialApplicationId);
      await refreshCoreData(); // Refresh to get updated applicantUserId
    } catch (err) {
      console.error("attachUser - error:", err);
      AppToasterV2.showFailedActionToaster("Failed to attach user to financial application");
    } finally {
      isAttaching.current = false;
    }
  };

  useEffect(() => {
    // Only proceed if we have the required conditions
    if (!financialApplicationId || !isAuthenticated || clientType !== "consumer") {
      return;
    }

    // Only attempt attachment once per financial application
    if (hasAttemptedAttachment.current) {
      return;
    }

    // Wait for core data to be loaded
    if (!coreData) {
      return;
    }

    // Check if user is already attached
    if (coreData.applicantUserId) {
      hasAttemptedAttachment.current = true;
      return;
    }

    // Attach user if not already attached
    hasAttemptedAttachment.current = true;
    attachUser();
  }, [financialApplicationId, isAuthenticated, clientType, coreData]);

  return {
    isAttached: !!coreData?.applicantUserId,
    isAttaching: isAttaching.current,
  };
};
